import * as d3 from 'd3';
import { versionColor } from '../colors.js';

// Horizontal axis: artifacts ordered by createdAt (newest left → oldest right).
// Baseline rects colored per kind/version; bars above = requests served.
// For dictionaries we can approximate hits via server.state.pinHits (not passed here); we use requestsServed on the asset for its versions.
// We aggregate: full-asset versions from snap.versions, deltas by delta key, and each entry gets a bar based on cumulative requests routed through it (approximated via per-asset stats).

export function createRequestsPerAssetChart(svgEl) {
  const svg = d3.select(svgEl);
  svg.append('g').attr('class', 'bars');
  svg.append('g').attr('class', 'axis');

  function update(snap, perArtifactHits) {
    const bounds = svgEl.getBoundingClientRect();
    const W = bounds.width, H = bounds.height;
    svg.attr('viewBox', `0 0 ${W} ${H}`);

    const items = [];
    for (const v of snap.versions) items.push({ key: `V:${v.key}`, createdAt: v.createdAt, kind: 'asset', assetId: v.assetId, v: v.version, hits: perArtifactHits?.get(`V:${v.key}`) ?? 0 });
    for (const d of snap.dictionaries) items.push({ key: `D:${d.id}`, createdAt: d.createdAt, kind: 'dict', assetId: d.sourceAssetId, v: d.sourceVersion, hits: perArtifactHits?.get(`D:${d.id}`) ?? 0 });
    for (const d of snap.deltas) items.push({ key: `Δ:${d.id}`, createdAt: d.createdAt, kind: 'delta', assetId: d.assetId, fromV: d.fromVersion, toV: d.toVersion, hits: perArtifactHits?.get(`Δ:${d.id}`) ?? 0 });
    items.sort((a, b) => b.createdAt - a.createdAt); // newest left

    const marginL = 8, marginR = 8, marginB = 22, marginT = 8;
    const boxSize = 10;
    const pad = 2;
    const maxItems = Math.floor((W - marginL - marginR) / (boxSize + pad));
    const display = items.slice(0, maxItems);
    const maxHits = Math.max(1, d3.max(display, (d) => d.hits) ?? 1);
    const barMax = H - marginB - marginT - boxSize - 2;

    const g = svg.select('g.bars');
    const sel = g.selectAll('g.a').data(display, (d) => d.key);
    const enter = sel.enter().append('g').attr('class', 'a');
    enter.append('rect').attr('class', 'bar').attr('width', boxSize);
    enter.append('rect').attr('class', 'box').attr('width', boxSize).attr('height', boxSize).attr('rx', 2);
    const merged = sel.merge(enter);
    merged.attr('transform', (_, i) => `translate(${marginL + i * (boxSize + pad)}, ${marginT})`);
    merged.select('rect.bar')
      .attr('y', (d) => barMax - (d.hits / maxHits) * barMax)
      .attr('height', (d) => (d.hits / maxHits) * barMax)
      .attr('fill', (d) => d.kind === 'delta' ? versionColor(d.assetId, d.toV) : versionColor(d.assetId, d.v))
      .attr('opacity', 0.8);
    merged.select('rect.box')
      .attr('y', barMax + 2)
      .attr('fill', (d) => d.kind === 'delta' ? versionColor(d.assetId, d.toV) : versionColor(d.assetId, d.v))
      .attr('stroke', (d) => d.kind === 'dict' ? '#f0b429' : 'none')
      .attr('stroke-width', 1.5)
      .attr('opacity', (d) => d.kind === 'delta' ? 0.9 : 1);
    merged.append('title').text((d) => `${d.key}\nhits=${d.hits}`);
    sel.exit().remove();

    svg.select('g.axis').selectAll('text').data([`${display.length}/${items.length} shown (newest → oldest)`]).join('text')
      .attr('x', marginL).attr('y', H - 6).attr('fill', '#8b949e').attr('font-size', 10).text((d) => d);
  }
  return { update };
}
